import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextInput,
  TextAreaInput,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";
import UserCocSelector from "@/components/Forms/components/Selectors/UserCocSelector";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import type {
  BaseCocModalProps,
  UserOption,
  CocSignatureObject,
} from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

interface ReleaseCocModalProps extends BaseCocModalProps {}

interface ReleaseFormData {
  released_by: UserOption | null;
  released_to: string;
  timestamp: string;
  reason: string;
}

const releaseValidationSchema = z.object({
  released_by: z
    .object(
      {
        user_id: z.number(),
        full_name: z.string(),
      },
      {
        required_error: "Released by user is required",
      }
    )
    .refine((val) => val !== null && val !== undefined, {
      message: "Released by user is required",
    }),
  released_to: z
    .string({
      required_error: "Released to is required",
    })
    .min(1, { message: "Released to is required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
  reason: z.string().optional(),
});

const ReleaseCocModal: React.FC<ReleaseCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ReleaseFormData>({
    mode: "uncontrolled",
    initialValues: {
      released_by: null,
      released_to: "",
      timestamp: new Date().toISOString(),
      reason: "",
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const releasedToSig = useRef<SignatureFieldRef>();
  const releasedBySig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(releaseValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_to_sig: createSignature(
          releasedToSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
        custody_from_sig: createSignature(
          releasedBySig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: 1,
          new_location_name: formData.released_to,
          current_location_name: formData.released_by?.full_name || "",
          reason: formData.reason || "Evidence Released",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Release",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(`Evidence Item Released to ${formData.released_to}`, {
        variant: "success",
      });

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error releasing evidence:", error);
      enqueueSnackbar(`Error releasing evidence item.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Release Evidence Item</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <UserCocSelector
            label="Released By"
            required
            placeholder="Select the user releasing this evidence..."
            enabled={show}
            key={form.key("released_by")}
            {...form.getInputProps("released_by")}
          />
          <TextInput
            label="Released To"
            required
            placeholder="Enter name..."
            key={form.key("released_to")}
            {...form.getInputProps("released_to")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <TextAreaInput
            label="Release Notes"
            minRows={3}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={releasedBySig}
              label="Released By Signature"
              useESignature={true}
            />
          </div>
          <div>
            <SignatureField
              sigFieldRef={releasedToSig}
              label="Released To Signature"
              useESignature={false}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Release Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default ReleaseCocModal;
